import React, { useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  // useLocation,
  useNavigate,
} from 'react-router-dom';
import './App.css';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import EnrollForm from './EnrollForm';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import PublicLayout from './layouts/PublicLayout';
import HomePage from './HomePage';
import AssociatedWith from './AssociatedWith';
// import AwardsGallery from './AwardsGallery';
// import Webinars from './components/Webinars';
import Certification from './components/Certification';
import AboutMentor from './components/aboutmentor';
import CoursesAndResources from './CoursesAndResources';
import FeedbackReports from './FeedbackReports';
import MyMentees from './MyMentees';
import DiscussionThreads from './DiscussionThreads';
import ReportsAnalytics from './ReportsAnalytics';
import FeedbackFlags from './FeedbackFlags';

import StudentDashboard from './StudentDashboard';
import CoursesPage from './Courses';
import MyDiscussions from './MyDiscussions';
import MentorshipDashboard from './MentorshipDashboard';
import AdminDashboard from './AdminDashboard';

import StudentHeader from './StudentHeader';
import MentorHeader from './MentorHeader';
import AdminHeader from './AdminHeader';

import StudentLayout from './layouts/StudentLayout';
import MentorLayout from './layouts/MentorLayout';
import AdminLayout from './layouts/AdminLayout';

import RefundPolicy from './RefundPolicy';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfUse from './TermsOfUse';

export default function App() {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [isEnrollOpen, setIsEnrollOpen] = useState(false);

  return (
    <Router>
      <AppLayout
        isLoginOpen={isLoginOpen}
        setIsLoginOpen={setIsLoginOpen}
        isSignupOpen={isSignupOpen}
        setIsSignupOpen={setIsSignupOpen}
        isEnrollOpen={isEnrollOpen}
        setIsEnrollOpen={setIsEnrollOpen}
      />
    </Router>
  );
}

function AppLayout(props) {
  // const { pathname } = useLocation();
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole');

  // --- This Header logic is now simplified ---
  let HeaderCmp = null;
  if (userRole === 'student') {
    HeaderCmp = StudentHeader;
  } else if (userRole === 'mentor') {
    HeaderCmp = MentorHeader;
  } else if (userRole === 'admin') {
    HeaderCmp = AdminHeader;
  }
  // The PublicHeader will be handled inside the PublicLayout component

  const openLogin = () => {
    props.setIsSignupOpen(false);
    props.setIsLoginOpen(true);
  };

  const openSignup = () => {
    props.setIsLoginOpen(false);
    props.setIsSignupOpen(true);
  };

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    navigate('/');
    window.location.reload(); // Keep this to ensure a clean state
  };

  return (
    <>
      {/* Conditionally render the correct header based on role */}
      {HeaderCmp && (
        <HeaderCmp
          userRole={userRole}
          setIsLoginOpen={openLogin}
          setIsSignupOpen={openSignup}
          logout={logout}
        />
      )}

      <Routes>
        {/* --- Block 1: Routes for users NOT logged in --- */}
        {!userRole && (
          <Route
            element={
              <PublicLayout
                setIsLoginOpen={props.setIsLoginOpen}
                setIsSignupOpen={props.setIsSignupOpen}
              />
            }
          >
            <Route path="/" element={<HomePage setIsEnrollOpen={props.setIsEnrollOpen} />} />
            <Route path="/courses" element={<CoursesPage openEnroll={() => props.setIsEnrollOpen(true)} />} />
            <Route path="/certifications" element={<Certification setIsEnrollOpen={props.setIsEnrollOpen} />} />
            <Route path="/aboutmentor" element={<AboutMentor />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/associated-with" element={<AssociatedWith />} />

            {/* If a non-logged-in user tries any other path, redirect them to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        )}

        {/* --- Block 2: Routes ONLY for STUDENTS --- */}
        {userRole === 'student' && (
          <Route element={<StudentLayout />}>
            <Route path="/student/dashboard" element={<StudentDashboard />} />
            <Route path="/student/discussions" element={<MyDiscussions />} />
            <Route path="/student/courses" element={<CoursesPage openEnroll={() => props.setIsEnrollOpen(true)} />} />
            <Route path="/student/certifications" element={<Certification setIsEnrollOpen={props.setIsEnrollOpen} />} />
            <Route path="/student/aboutmentor" element={<AboutMentor />} />
            {/* The homepage for a logged-in student should be their dashboard */}
            <Route path="/" element={<Navigate to="/student/dashboard" replace />} />
            {/* If a student tries any other unknown path, send them to their dashboard */}
            <Route path="*" element={<Navigate to="/student/dashboard" replace />} />
          </Route>
        )}

        {/* --- Block 3: Routes ONLY for MENTORS --- */}
        {userRole === 'mentor' && (
          <Route element={<MentorLayout />}>
            <Route path="/mentor/dashboard" element={<MentorshipDashboard />} />
            <Route path="/mentor/courses-resources" element={<CoursesAndResources />} />
            <Route path="/mentor/feedback-reports" element={<FeedbackReports />} />
            <Route path="/mentor/my-mentees" element={<MyMentees />} />
            <Route path="/mentor/discussion-threads" element={<DiscussionThreads />} />
            <Route path="/" element={<Navigate to="/mentor/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/mentor/dashboard" replace />} />
          </Route>
        )}

        {/* --- Block 4: Routes ONLY for ADMINS --- */}
        {userRole === 'admin' && (
          <Route element={<AdminLayout />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/reports-analytics" element={<ReportsAnalytics />} />
            <Route path="/admin/feedback-flags" element={<FeedbackFlags />} />
            <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
          </Route>
        )}

        {/* --- General policy routes accessible to anyone --- */}
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/loginform" element={<LoginForm />} />
        <Route path="/signupform" element={<SignupForm />} />
        <Route path="/home" element={<HomePage setIsEnrollOpen={props.setIsEnrollOpen} />} />


      </Routes>

      {/* --- This Auth Popup logic remains the same --- */}
      {props.isLoginOpen && (
        <div className="login-overlay" onClick={e => e.target.classList.contains('login-overlay') && props.setIsLoginOpen(false)}>
          <LoginForm close={() => props.setIsLoginOpen(false)} openSignup={openSignup} />
        </div>
      )}
      {props.isSignupOpen && (
        <div className="login-overlay" onClick={e => e.target.classList.contains('login-overlay') && props.setIsSignupOpen(false)}>
          <SignupForm close={() => props.setIsSignupOpen(false)} openLogin={openLogin} />
        </div>
      )}
      {props.isEnrollOpen && <EnrollForm onClose={() => props.setIsEnrollOpen(false)} />}
    </>
  );
}